import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./QuestionTypes.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { handleErrorMessage } from "../../../../../redux/actions/classes";
import { useDispatch } from "react-redux";
import { validateQuestion } from "../../../../../utils";
import { useTranslation } from "react-i18next";

const FillInTheBlankQuestion = forwardRef(
  ({ initialData = {}, onSave, onCancel, onMoveUp, onMoveDonw, onDeleteQuestion }, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [questionText, setQuestionText] = useState("");
    const [answers, setAnswers] = useState([""]);
    const [correctFlags, setCorrectFlags] = useState([false]); // parallel array: true if that answer is marked correct
    const [maxMarks, setMaxMarks] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(null);

    const containerRef = useRef(null);
    const questionRef = useRef(null);

    // Helper: preserve incoming IDs if present
    function generateChoiceId(baseId, index) {
      if (Array.isArray(initialData.choices) && initialData.choices[index]?.id) {
        return initialData.choices[index].id;
      }
      if (Array.isArray(initialData.choice) && initialData.choice[index]?.id) {
        return initialData.choice[index].id;
      }
      return `fib-${initialData.id ?? Date.now()}-${index}`;
    }

    // Sync initialData only once on mount (supports multiple legacy shapes)
    const initializedRef = useRef(false);
    useEffect(() => {
      if (initializedRef.current || !initialData) return;
      initializedRef.current = true;

      setQuestionText(initialData.questionText ?? initialData.text ?? initialData.question ?? "");

      // Build answers list and correct flags from incoming shapes
      const incomingAnswers = [];
      const incomingCorrectFlags = [];

      if (Array.isArray(initialData.choices) && initialData.choices.length > 0) {
        initialData.choices.forEach((c) => {
          // Determine textual answer candidate
          const cc = c.correctChoice ?? c.correct_choice;
          const textCandidate =
            c.choiceText ??
            c.choice_text ??
            c.text ??
            (cc !== undefined && cc !== null ? String(cc) : "") ??
            "";
          incomingAnswers.push(String(textCandidate ?? "").trim());

          // Determine correctness
          const isCorrect = !!(c.isCorrect ?? c.is_correct);
          incomingCorrectFlags.push(isCorrect);
        });
      } else if (Array.isArray(initialData.choice) && initialData.choice.length > 0) {
        // Legacy shape: often the first item contains correct_list
        const first = initialData.choice[0];
        if (Array.isArray(first?.correct_list) && first.correct_list.length > 0) {
          // collect correct_list strings
          const correctList = first.correct_list.map((x) => String(x.correct_choice ?? x).trim());
          // If there are explicit choices in initialData.choice (not common), try to map them
          // We'll set answers to correctList by default
          correctList.forEach((c) => {
            if (c) {
              incomingAnswers.push(c);
              incomingCorrectFlags.push(true);
            }
          });
        } else {
          // fallback: use any choice_text / correct_choice fields
          initialData.choice.forEach((c) => {
            const txt = c.choice_text ?? c.choiceText ?? c.text ?? "";
            incomingAnswers.push(String(txt).trim());
            incomingCorrectFlags.push(!!(c.isCorrect ?? c.is_correct));
          });
        }
      } else if (Array.isArray(initialData.correct_list) && initialData.correct_list.length > 0) {
        initialData.correct_list.forEach((c) => {
          if (c && c.correct_choice !== undefined && c.correct_choice !== null) {
            incomingAnswers.push(String(c.correct_choice).trim());
            incomingCorrectFlags.push(true);
          }
        });
      } else if (Array.isArray(initialData.answers) && initialData.answers.length > 0) {
        initialData.answers.forEach((a) => {
          if (a !== undefined && a !== null && String(a).trim() !== "") {
            incomingAnswers.push(String(a));
            incomingCorrectFlags.push(false); // we don't know if they were marked correct
          }
        });
      }

      // Finalize
      if (incomingAnswers.length > 0) {
        // ensure no empties
        const cleaned = incomingAnswers.map((s) => String(s).trim()).filter(Boolean);
        if (cleaned.length) {
          setAnswers(cleaned);
          // if incomingCorrectFlags shorter than cleaned, pad with false
          const paddedFlags = cleaned.map((_, i) => !!incomingCorrectFlags[i]);
          setCorrectFlags(paddedFlags);
        } else {
          setAnswers([""]);
          setCorrectFlags([false]);
        }
      } else {
        setAnswers([""]);
        setCorrectFlags([false]);
      }

      const mm = initialData.maxMarks ?? initialData.marks ?? initialData.max_marks ?? "";
      setMaxMarks(mm === undefined || mm === null ? "" : String(mm));
      setIsRequired(!!(initialData.isMandatory ?? initialData.is_mandatory ?? initialData.is_required));
    }, [initialData]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setFocusedIndex(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleAddBlankToQuestion = () => {
      const textarea = questionRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart ?? textarea.value.length;
      const end = textarea.selectionEnd ?? textarea.value.length;
      const newText = questionText.substring(0, start) + "_____" + questionText.substring(end);
      setQuestionText(newText);
      setTimeout(() => {
        if (textarea.setSelectionRange) {
          textarea.setSelectionRange(start + 5, start + 5);
        }
        textarea.focus();
      }, 0);
    };

    const handleAddAnswer = () => {
      setAnswers((prev) => [...prev, ""]);
      setCorrectFlags((prev) => [...prev, false]);
      setFocusedIndex(answers.length);
    };

    const handleAnswerChange = (index, value) => {
      setAnswers((prev) => {
        const updated = [...prev];
        updated[index] = value;
        return updated;
      });
    };

    const handleRemoveAnswer = (index) => {
      setAnswers((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated.length ? updated : [""];
      });
      setCorrectFlags((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated.length ? updated : [false];
      });
    };

    const toggleCorrectFlag = (index) => {
      setCorrectFlags((prev) => {
        const next = [...prev];
        next[index] = !next[index];
        return next;
      });
    };

    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    const handleSave = () => {
      // Only consider non-empty answers
      const options = answers.map((a) => (typeof a === "string" ? a.trim() : a)).filter(Boolean);
      const flags = correctFlags.slice(0, options.length);

      // Validate using your util: correctAnswer can be indices of flagged true
      const correctIndices = flags.map((f, idx) => (f ? idx : -1)).filter((i) => i >= 0);
      const errorMessage = validateQuestion({
        question: questionText,
        marks: maxMarks,
        options,
        correctAnswer: correctIndices, // send indices to validator
        type: "fillintheblank",
      });

      if (errorMessage) {
        dispatch(handleErrorMessage(errorMessage));
        return null;
      }

      // Normalized choices: each answer becomes a choice object (editor-friendly)
      const normalizedChoices = options.map((ans, idx) => ({
        id: generateChoiceId(initialData.id, idx),
        choiceText: ans,
        isCorrect: !!flags[idx],
      }));

      // Legacy structure: single placeholder choice with correct_list containing only flagged answers
      const correctList = options
        .map((ans, idx) => (flags[idx] ? { correct_choice: ans } : null))
        .filter(Boolean);

      const legacyChoice = [
        {
          choice_text: "enhancedQuiz.blankAnswer",
          correct_choice: (correctList[0] && correctList[0].correct_choice) || "",
          is_correct: false,
          img: "",
          correct_list: correctList,
        },
      ];

      const payload = {
        id: initialData.id ?? `q-${Date.now()}`,
        type: "fillBlanks",
        questionText: questionText?.trim() ?? "",
        choices: normalizedChoices,
        isMandatory: !!isRequired,
        maxMarks: parseInt(maxMarks, 10) || 0,
        icon: initialData.icon ?? null,
        // include legacy structure for older endpoints (now accurate)
        choice: legacyChoice,
      };

      return payload;
    };

    return (
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionType}>
            <div className={styles.radioIcon}>
              <div className={styles.radioInner}></div>
            </div>
            <span>{t("quiz.fillInTheBlanks")}</span>
          </div>

          <div className={styles.questionActions}>
            <button className={styles.actionButton} onClick={onMoveUp} type="button">
              <KeyboardArrowUpIcon />
            </button>
            <button className={styles.actionButton} onClick={onMoveDonw} type="button">
              <KeyboardArrowDownIcon />
            </button>
            <button className={styles.actionButton} onClick={onDeleteQuestion} type="button">
              <DeleteOutlineIcon />
            </button>
            <div className={styles.marksContainer}>
              <span>{t("quiz.maxMarks")}</span>
              <input
                min={"0"}
                type="number"
                className={styles.marksInput}
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
            <div className={styles.requiredContainer}>
              <span>{t("quiz.required")}</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.questionContent}>
          <div className={styles.questionRow}>
            <input
              className={styles.questionInput}
              placeholder={t("quiz.question")}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              ref={questionRef}
            />
            <button className={styles.addBlankBtn} onClick={handleAddBlankToQuestion} type="button">
              {t("quiz.addBlankSpaceBtn")}
            </button>
          </div>

          <div className={styles.optionsContainer} ref={containerRef}>
            {answers.map((answer, index) => (
              <div key={index} className={styles.optionItem}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%" }}>
                  <input
                    type="text"
                    className={styles.optionInput}
                    value={answer}
                    placeholder={`${t("quiz.blankAnswer")} ${index + 1}`}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    onFocus={() => setFocusedIndex(index)}
                  />

                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!correctFlags[index]}
                      onChange={() => toggleCorrectFlag(index)}
                      title={t("quiz.markAsCorrect")}
                    />
                    <span style={{ fontSize: 12 }}>{t("quiz.correctAnswer")}</span>
                  </label>

                  {answers.length > 1 && (
                    <button className={styles.removeOptionButton} onClick={() => handleRemoveAnswer(index)} type="button">
                      <CloseIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button className={styles.addOptionButton} onClick={handleAddAnswer} type="button">
              <AddCircleOutlineIcon sx={{ color: "#F33070" }} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default FillInTheBlankQuestion;
